import { Parser } from 'htmlparser2'
import sanitizeHtml from 'sanitize-html'

/** The contract's rich-text allowlist for `lead` and row `body`; anything else is stripped. */
const FORM_HTML: sanitizeHtml.IOptions = {
  allowedTags: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li'],
  allowedAttributes: { a: ['href'] },
  allowedSchemes: ['https', 'mailto'],
  allowedSchemesAppliedToAttributes: ['href'],
  allowProtocolRelative: false,
  // Dropped with their content, not unwrapped to text.
  nonTextTags: [
    'script',
    'style',
    'textarea',
    'option',
    'noscript',
    'svg',
    'math',
    'template',
    'iframe',
    'object',
  ],
}

// sanitize-html checks schemes only on an href that has one, so relative and fragment links pass it.
const SAFE_HREF = /^(https:|mailto:)/i
const safeHref = (attribs: sanitizeHtml.Attributes) => SAFE_HREF.test(attribs.href?.trim() ?? '')

// A second pass over clean HTML: a link that kept an https or mailto href gets rel="noopener"; any
// other becomes a span, which the allowlist then unwraps to its text.
const LINKS: sanitizeHtml.IOptions = {
  ...FORM_HTML,
  allowedAttributes: { a: ['href', 'rel'] },
  transformTags: {
    a: (_tagName, attribs): sanitizeHtml.Tag =>
      safeHref(attribs)
        ? { tagName: 'a', attribs: { href: attribs.href!, rel: 'noopener' } }
        : { tagName: 'span', attribs: {} },
  },
}

// The consent notice sits inside the donation flow, so its links open a new tab and the form stays.
const CONSENT_LINKS: sanitizeHtml.IOptions = {
  ...FORM_HTML,
  allowedAttributes: { a: ['href', 'rel', 'target'] },
  transformTags: {
    a: (_tagName, attribs): sanitizeHtml.Tag =>
      safeHref(attribs)
        ? {
            tagName: 'a',
            attribs: { href: attribs.href!, target: '_blank', rel: 'noopener noreferrer' },
          }
        : { tagName: 'span', attribs: {} },
  },
}

/**
 * The consent notice → the same allowlist as the rich text, with links opening a new tab. Markup
 * with no readable text (`<p><br></p>`) is empty, so the host falls back to its own notice.
 */
export function sanitizeConsentHtml(html: string): string {
  const clean = sanitizeHtml(sanitizeHtml(html, FORM_HTML), CONSENT_LINKS).trim()
  return htmlToText(clean) ? clean : ''
}

/** Form rich text → HTML holding only the contract's tags; a link keeps an https or mailto href. */
export const sanitizeFormHtml = (html: string): string =>
  sanitizeHtml(sanitizeHtml(html, FORM_HTML), LINKS).trim()

const LIST = new Set(['ul', 'ol'])

/**
 * Sanitized row HTML → the prose and the check-icon bullets, as fundraising-contentful's
 * splitLandingBody does for a Contentful row: each top-level list's items become plain-text
 * bullets (nested items folded into their parent's text), and the rest stays HTML.
 */
export function splitRowBody(html: string): { bodyHtml: string; bullets: string[] } {
  const clean = sanitizeFormHtml(html)
  const bullets: string[] = []
  const cut: Array<[number, number]> = []
  let depth = 0
  let listStart = -1
  let item: string | null = null
  const parser = new Parser({
    onopentag(name) {
      if (depth === 0 && LIST.has(name)) listStart = parser.startIndex
      if (listStart >= 0 && depth === 1 && name === 'li') item = ''
      // A line break inside an item still separates words, as the newline it stands for would.
      if (item !== null && name === 'br') item += ' '
      depth++
    },
    ontext(text) {
      if (item !== null) item += text
    },
    onclosetag(name) {
      depth--
      if (listStart >= 0 && depth === 1 && name === 'li') {
        const text = (item ?? '').replace(/\s+/g, ' ').trim()
        if (text) bullets.push(text)
        item = null
      }
      if (depth === 0 && listStart >= 0) {
        cut.push([listStart, parser.endIndex + 1])
        listStart = -1
      }
    },
  })
  parser.end(clean)
  let bodyHtml = clean
  for (const [start, end] of cut.reverse())
    bodyHtml = bodyHtml.slice(0, start) + bodyHtml.slice(end)
  return { bodyHtml: bodyHtml.trim(), bullets }
}

/** Rich text as plain text, for meta descriptions: tags gone, entities decoded, blocks spaced. */
export function htmlToText(html: string): string {
  let text = ''
  const parser = new Parser({
    ontext(chunk) {
      text += chunk
    },
    onopentag(name) {
      if (name === 'br' || name === 'p' || name === 'li') text += ' '
    },
  })
  parser.end(sanitizeFormHtml(html))
  return text.replace(/\s+/g, ' ').trim()
}
